import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axios';
import { Branch, User, UserBranch } from '../../types';

const UserBranchAssignment: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [assignments, setAssignments] = useState<UserBranch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [commissionRate, setCommissionRate] = useState<string>('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedBranchFilter) {
      fetchBranchUsers(selectedBranchFilter);
    } else {
      setAssignments([]);
    }
  }, [selectedBranchFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [branchesRes, usersRes] = await Promise.all([
        axiosInstance.get('/branches'),
        axiosInstance.get('/users')
      ]);
      setBranches(branchesRes.data);
      setUsers(usersRes.data);
      if (branchesRes.data.length > 0) {
        setSelectedBranchFilter(branchesRes.data[0].id);
      }
    } catch (error: any) {
      setError(error.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranchUsers = async (branchId: string) => {
    try {
      const response = await axiosInstance.get(`/branches/${branchId}/users`);
      setAssignments(response.data);
    } catch (error: any) {
      console.error('Error al cargar usuarios de sucursal:', error);
    }
  };

  const handleAssign = async () => {
    try {
      await axiosInstance.post('/branches/assign-user', {
        userId: selectedUserId,
        branchId: selectedBranchId,
        commissionRate: commissionRate ? parseFloat(commissionRate) : undefined
      });
      setSuccess('Usuario asignado correctamente');
      setOpenDialog(false);
      setSelectedUserId('');
      setSelectedBranchId('');
      setCommissionRate('');
      if (selectedBranchFilter) {
        fetchBranchUsers(selectedBranchFilter);
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Error al asignar usuario');
    }
  };

  const handleUnassign = async (userId: string, branchId: string) => {
    if (window.confirm('¿Estás seguro de que deseas desasignar este usuario de la sucursal?')) {
      try {
        await axiosInstance.post('/branches/unassign-user', { userId, branchId });
        setSuccess('Usuario desasignado correctamente');
        if (selectedBranchFilter) {
          fetchBranchUsers(selectedBranchFilter);
        }
        setTimeout(() => setSuccess(null), 3000);
      } catch (error: any) {
        setError(error.message || 'Error al desasignar usuario');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Asignación de Usuarios a Sucursales</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Asignar Usuario
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Filtrar por Sucursal</InputLabel>
        <Select
          value={selectedBranchFilter}
          onChange={(e) => setSelectedBranchFilter(e.target.value)}
          label="Filtrar por Sucursal"
        >
          {branches.map((branch) => (
            <MenuItem key={branch.id} value={branch.id}>
              {branch.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Comisión Personal (%)</TableCell>
              <TableCell>Fecha de Asignación</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No hay usuarios asignados a esta sucursal
                </TableCell>
              </TableRow>
            ) : (
              assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell>
                    {assignment.user
                      ? `${assignment.user.firstName} ${assignment.user.lastName}`
                      : '-'}
                  </TableCell>
                  <TableCell>{assignment.user?.email || '-'}</TableCell>
                  <TableCell>{assignment.user?.role || '-'}</TableCell>
                  <TableCell>
                    {assignment.commissionRate != null
                      ? `${assignment.commissionRate}%`
                      : 'Usa tasa de sucursal'}
                  </TableCell>
                  <TableCell>
                    {new Date(assignment.assignedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleUnassign(assignment.userId, assignment.branchId)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar Usuario a Sucursal</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Usuario</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              label="Usuario"
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} ({u.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Sucursal</InputLabel>
            <Select
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
              label="Sucursal"
            >
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Comisión Personal (%) - Opcional"
            type="number"
            value={commissionRate}
            onChange={(e) => setCommissionRate(e.target.value)}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            helperText="Si se deja vacío, se usará la tasa de comisión de la sucursal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleAssign}
            variant="contained"
            color="primary"
            disabled={!selectedUserId || !selectedBranchId}
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserBranchAssignment;
